/**
 * Per-route-group control over which visitors get a Datadog RUM session.
 *
 * Datadog bills per session and `sessionSampleRate` is a single init-time number
 * that cannot vary by route. The lever that does vary by route is view creation:
 * `nextjsPlugin()` forces `trackViewsManually`, so a session exists only once a
 * view is started. Deciding per route group whether to start the first view is
 * therefore the same thing as sampling sessions per route group.
 */

// Subpath, not the root barrel: the barrel pulls in the AWS MediaConvert
// client, which cannot load in this module's jsdom tests.
import { getEnvironment } from "@repo/common/environment";

/**
 * The one place RUM session cost is tuned: percentage of browsing sessions that
 * start RUM views in each route group. 100 collects the group in full and costs
 * no cookie work at all; 0 disables it entirely.
 */
export const RUM_ROUTE_GROUP_SAMPLE_RATES = {
  platform: 100,
  public: 100,
  docs: 100,
  internal: 100,
  marketing: 20,
} as const;

export type RumRouteGroup = keyof typeof RUM_ROUTE_GROUP_SAMPLE_RATES;

/**
 * RUM global-context key carrying the route group, so marketing traffic can be
 * separated from app traffic in Datadog (`@context.route_group`). The view name
 * cannot serve this purpose: route groups are not part of the URL, so they never
 * appear in the name the Next.js plugin derives from the pathname.
 */
export const RUM_ROUTE_GROUP_CONTEXT_KEY = "route_group";

/**
 * Groups exempt from the bot heuristic below. `(platform)` is not
 * authentication-only; it also serves `/login`, `/join` and `/invitations`, so
 * the exemption is about cost asymmetry, not reachability: a false positive here
 * drops a real sign-in session.
 */
const HEURISTIC_EXEMPT: ReadonlySet<RumRouteGroup> = new Set([
  "platform",
  "internal",
]);

/**
 * User agents that must not consume a RUM session. Most crawlers never execute
 * JavaScript and so never reach this code; this only has to catch the ones that
 * do. Every token is one no real browser sends: a false positive silently
 * discards a real visitor, which is worse than paying for a bot.
 */
const NON_HUMAN_USER_AGENT =
  /\bbot\b|bot\/|crawler|spider|slurp|headless|lighthouse|facebookexternalhit|embedly|bingpreview|pingdom|ahrefs|semrush|baiduspider|python-requests|curl\/|wget\/|node-fetch|go-http-client|okhttp/i;

/** True for crawlers, link previewers and browser automation. */
export function isNonHumanClient(client: {
  userAgent: string;
  webdriver?: boolean;
}): boolean {
  return (
    client.webdriver === true || NON_HUMAN_USER_AGENT.test(client.userAgent)
  );
}

/** The explicit opt-in `(public)/g/[slug]` uses for its embeddable render. */
export function isEmbedRequested(search: string): boolean {
  return (
    search.includes("embed=") &&
    new URLSearchParams(search).get("embed") === "true"
  );
}

/**
 * `(public)/g/[slug]` is built to be embedded in customer sites, so without this
 * every visitor to a third-party page hosting a published guide would become a
 * billed RUM session: volume nothing on our side bounds or can even estimate.
 *
 * Cross-origin access to `window.top` throws, and a throw means framed.
 */
function isEmbedded(): boolean {
  try {
    if (window.self !== window.top) {
      return true;
    }
  } catch {
    return true;
  }
  return isEmbedRequested(window.location.search);
}

const SAMPLE_COOKIE = "dd_rum_sample";

/** Both mirror `_dd_s`, so the decision expires exactly when the session does. */
const INACTIVITY_TTL_SECONDS = 15 * 60;
const MAX_AGE_MS = 4 * 60 * 60 * 1000;

/**
 * A cookie, not `sessionStorage`: Datadog bills per session and a session is
 * shared across tabs via `_dd_s`, so a per-tab decision would let any tab that
 * rolled in start a session all tabs then share.
 *
 * Stored as `<createdAt>:<roll>` rather than the roll's outcome, so a visitor
 * moving between two sampled groups reuses the same roll instead of getting a
 * fresh chance at each one.
 */
function readRoll(now: number): { createdAt: number; roll: number } | null {
  const raw = new RegExp(`(?:^|;\\s*)${SAMPLE_COOKIE}=([^;]*)`).exec(
    document.cookie,
  )?.[1];
  if (!raw) {
    return null;
  }
  const parts = raw.split(":");
  const createdAt = Number(parts[0]);
  const roll = Number(parts[1]);
  if (
    parts.length !== 2 ||
    !Number.isFinite(createdAt) ||
    !Number.isFinite(roll) ||
    now - createdAt > MAX_AGE_MS
  ) {
    // Malformed, or past the maximum session length: Datadog bills a new
    // session at that point, so this one gets a fresh roll.
    return null;
  }
  return { createdAt, roll };
}

function writeRoll(createdAt: number, roll: number): void {
  // Secure keyed off the actual scheme rather than NODE_ENV, so it is correct on
  // preview and on http://*.localhost alike.
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${SAMPLE_COOKIE}=${createdAt}:${roll}; path=/; max-age=${INACTIVITY_TTL_SECONDS}; samesite=lax${secure}`;
}

/**
 * Memoised for the lifetime of the document: neither the user agent nor the
 * frame/embed state can change, and `DatadogRumViews` remounts on every
 * cross-group navigation.
 */
let embedded: boolean | undefined;
let nonHuman: boolean | undefined;

/** Test seam: the suites mutate `navigator.userAgent` and the cookie between cases. */
export function resetRumSamplingMemo(): void {
  embedded = undefined;
  nonHuman = undefined;
}

/**
 * Whether this client should start RUM views, and therefore a RUM session,
 * while inside `routeGroup`.
 *
 * Client-only: returns `false` during server rendering, where there is no
 * browsing session to sample.
 */
export function shouldStartRumViews(routeGroup: RumRouteGroup): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // A dev host only reaches an initialised SDK when a developer set
  // NEXT_PUBLIC_DD_RUM_DEV, so collect every view here: a locally sampled-out
  // or bot-filtered session is indistinguishable from broken instrumentation,
  // which is the one thing the opt-in exists to rule out.
  if (getEnvironment() === "dev") {
    return true;
  }

  embedded ??= isEmbedded();
  if (embedded) {
    return false;
  }

  if (!HEURISTIC_EXEMPT.has(routeGroup)) {
    nonHuman ??= isNonHumanClient(window.navigator);
    if (nonHuman) {
      return false;
    }
  }

  const rate = RUM_ROUTE_GROUP_SAMPLE_RATES[routeGroup];
  if (rate >= 100) {
    return true;
  }
  if (rate <= 0) {
    return false;
  }

  // Only sampled groups reach the cookie, so the fully-collected groups,
  // including `platform`, the highest-volume one, pay no cookie I/O.
  const now = Date.now();
  const existing = readRoll(now);
  if (existing) {
    // Slide the TTL so the decision expires with the session rather than at a
    // fixed time after it began.
    writeRoll(existing.createdAt, existing.roll);
    return existing.roll < rate;
  }

  // Cookies blocked means `_dd_s` cannot persist either, so RUM has no session
  // to sample and the unstored roll costs nothing.
  const roll = Math.random() * 100;
  writeRoll(now, roll);
  return roll < rate;
}
